/**
 * The words a /query/v2 verdict is shown with. The claim's citation chip carries the
 * verdict colour and these words as its tooltip; the same words are read to screen
 * readers, so the meaning never rests on colour alone.
 */
import type { TFunction } from "i18next";

import type { UnverifiedReason, V2Mark, V2Status } from "@/lib/queryV2.ts";
import type { V2MarkLabel } from "./v2ClaimMarks.ts";

const KNOWN_REASONS: ReadonlySet<string> = new Set<UnverifiedReason>([
  "deadline",
  "budget",
  "judge_unavailable",
  "unparseable",
  "disputed",
]);

export function v2MarkLabeller(t: TFunction): (mark: V2Mark) => V2MarkLabel {
  return (mark) => {
    switch (mark.verdict) {
      case "supported":
        return {
          title: t("answerV2.supportedTitle"),
          flag: t("answerV2.verdict.supported"),
        };
      case "unsupported":
        return {
          title: t("answerV2.unsupportedTitle"),
          flag: t("answerV2.verdict.unsupported"),
        };
      case "unverified": {
        // `disputed` WAS checked: the batched judge rejected it and the recheck of the
        // claim alone found it supported (#1013), so "not checked" would be untrue.
        if (mark.reason === "disputed") {
          return {
            title: t("answerV2.disputedTitle", { reason: t("answerV2.reason.disputed") }),
            flag: t("answerV2.verdict.unverified"),
          };
        }
        // Streamed claims are not shape-checked, so a reason this client does not know
        // falls back to the plain title rather than showing a raw i18n key.
        const reason =
          mark.reason && KNOWN_REASONS.has(mark.reason) ? t(`answerV2.reason.${mark.reason}`) : null;
        return {
          title: reason
            ? t("answerV2.unverifiedTitleReason", { reason })
            : t("answerV2.unverifiedTitle"),
          flag: t("answerV2.verdict.unverified"),
        };
      }
      case "uncited":
        return { title: t("answerV2.uncitedTitle"), flag: t("answerV2.verdict.uncited") };
      case "invalid_citation":
        return {
          title: t("answerV2.invalidCitationTitle"),
          flag: t("answerV2.verdict.invalid_citation"),
        };
      case "from_conversation":
        return {
          title: t("answerV2.fromConversationTitle"),
          flag: t("answerV2.verdict.from_conversation"),
        };
    }
  };
}

/** The label a copied answer keeps after a claim; `null` for a verified one. */
export function v2CopyLabel(t: TFunction): (mark: V2Mark) => string | null {
  return (mark) => (mark.verdict === "supported" ? null : t(`answerV2.verdict.${mark.verdict}`));
}

const V2_REFUSALS = new Set([
  "query_budget_exhausted",
  "context_budget_exhausted",
  "model_timeout",
  "mcp_source_unavailable",
  "source_out_of_scope",
  "source_scope_empty",
  "source_scope_unavailable",
  "unparseable_completion",
]);

/** Title and body of a loop-level refusal; unknown reasons keep a generic copy. */
export function v2RefusalCopy(t: TFunction, reason: string): { title: string; body: string } {
  if (V2_REFUSALS.has(reason)) {
    return { title: t(`answerV2.refusal.${reason}.title`), body: t(`answerV2.refusal.${reason}.body`) };
  }
  return { title: t("answer.fallbackTitleUnknown"), body: t("answer.fallbackUnknown") };
}

/** The one-line count under a v2 answer and on its trace step. An answer with no claim
 *  (a greeting) has nothing to count, and "0 dari 0" read as a failed check. */
export function v2FootText(t: TFunction, status: V2Status): string {
  if (status.kind === "unchecked") return t("answerV2.footUnchecked");
  if (status.total === 0) return t("answerV2.footNoClaims");
  return t("answerV2.footSupported", { supported: status.supported, total: status.total });
}
